import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SessionState, MaterializedView, ScenarioDefinition, ScenarioDecision } from '@/types';
import { api } from '@/services/api';
import EmailInterface from '@/components/Email/EmailInterface';
import SlackInterface from '@/components/Slack/SlackInterface';
import CalendarView from '@/components/Calendar/CalendarView';
import ContactsView from '@/components/Contacts/ContactsView';
import PhoneView from '@/components/Phone/PhoneView';
import SessionCompletionModal from '@/components/Dashboard/SessionCompletionModal';
import ActivityFeed, { FeedEntry } from '@/components/Dashboard/ActivityFeed';
import DecisionModal from '@/components/Dashboard/DecisionModal';
import {
    Settings,
    Mail,
    Call,
    Forum,
    AccessTime,
    Warning,
    CalendarMonth,
    People,
    Assignment,
    Favorite,
    Groups
} from '@mui/icons-material';
import { Dialog } from '@mui/material';

interface SimulationDashboardProps {
    session: SessionState;
    materializedView: MaterializedView;
}

const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getInitials = (name: string): string => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Professional headshot photos for NPCs
const NPC_AVATARS: Record<string, string> = {
    'jennifer-martinez': 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face',
    'marcus-johnson': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
    'sarah-chen': 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face',
};

const StitchDashboard: React.FC<SimulationDashboardProps> = ({ session, materializedView }) => {
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [showSlackModal, setShowSlackModal] = useState(false);
    const [showCalendarModal, setShowCalendarModal] = useState(false);
    const [showContactsModal, setShowContactsModal] = useState(false);
    const [showPhoneModal, setShowPhoneModal] = useState(false);
    const [selectedNpcId, setSelectedNpcId] = useState<string | null>(null);
    const [composeToEmail, setComposeToEmail] = useState<string | undefined>(undefined);
    const [showDecisionHistory, setShowDecisionHistory] = useState(false);
    const [completingSession, setCompletingSession] = useState(false);

    // Scenario data
    const [scenario, setScenario] = useState<ScenarioDefinition | null>(null);

    // Decision state
    const [completedDecisions, setCompletedDecisions] = useState<Map<string, { optionId: string; feedback: string }>>(new Map());
    const [activeDecision, setActiveDecision] = useState<ScenarioDecision | null>(null);
    const [decisionMinimized, setDecisionMinimized] = useState(false);
    const [pendingDecision, setPendingDecision] = useState(false);
    const [feedEntries, setFeedEntries] = useState<FeedEntry[]>([]);

    // Server-synced time from MV
    const serverTimeRemaining = materializedView.timeRemainingSeconds ?? session.timeRemaining;
    const serverSimTime = materializedView.simulationTimeSeconds ?? 0;
    const isSessionCompleted = materializedView.sessionStatus === 'Completed' || session.status === 'Completed';

    // Local countdown
    const [localTimeRemaining, setLocalTimeRemaining] = useState(serverTimeRemaining);
    const [localSimTime, setLocalSimTime] = useState(serverSimTime);
    const lastServerTimeRef = useRef(serverTimeRemaining);

    useEffect(() => {
        if (serverTimeRemaining !== lastServerTimeRef.current) {
            setLocalTimeRemaining(serverTimeRemaining);
            setLocalSimTime(serverSimTime);
            lastServerTimeRef.current = serverTimeRemaining;
        }
    }, [serverTimeRemaining, serverSimTime]);

    useEffect(() => {
        if (isSessionCompleted || localTimeRemaining <= 0) return;
        const interval = setInterval(() => {
            setLocalTimeRemaining(prev => Math.max(0, prev - 1));
            setLocalSimTime(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [isSessionCompleted, localTimeRemaining <= 0]);

    // Load scenario on mount
    useEffect(() => {
        const loadScenario = async () => {
            try {
                const data = await api.getScenario(session.scenarioId);
                setScenario(data);
            } catch (err) {
                console.error('Failed to load scenario:', err);
            }
        };
        loadScenario();
    }, [session.scenarioId]);

    // Recover decisions from MV on load + initialize feed
    useEffect(() => {
        if (!scenario) return;

        const recovered = new Map<string, { optionId: string; feedback: string }>();
        const mvDecisions = materializedView.completedDecisions || [];

        for (const cd of mvDecisions) {
            const decision = scenario.decisions.find(d => d.id === cd.decisionId);
            const option = decision?.options.find(o => o.id === cd.optionId);
            recovered.set(cd.decisionId, {
                optionId: cd.optionId,
                feedback: cd.feedback || option?.feedback || ''
            });
        }

        setCompletedDecisions(recovered);

        // Build initial feed entries
        const entries: FeedEntry[] = [];
        const now = new Date().toISOString();

        entries.push({
            id: 'system-start',
            type: 'system',
            timestamp: session.startTime || now,
            message: `Simulation started: ${scenario.title}. You have ${Math.floor(scenario.duration / 60)} minutes. Check your inbox — you have unread messages that need attention.`
        });

        // Add completed decision outcomes to feed
        for (const cd of mvDecisions) {
            const decision = scenario.decisions.find(d => d.id === cd.decisionId);
            const option = decision?.options.find(o => o.id === cd.optionId);
            if (decision && option) {
                entries.push({
                    id: `outcome-${cd.decisionId}`,
                    type: 'decision_outcome',
                    timestamp: cd.timestamp || now,
                    prompt: decision.prompt,
                    chosenText: option.text,
                    feedback: cd.feedback || option.feedback
                });
            }
        }

        setFeedEntries(entries);

        // On recovery: show triggered-but-uncompleted decisions (minimized)
        const triggered = materializedView.triggeredDecisions || [];
        const completedIds = new Set(mvDecisions.map(cd => cd.decisionId));
        const pendingTriggered = triggered.filter(t => !completedIds.has(t.decisionId));

        if (pendingTriggered.length > 0) {
            const nextDecision = scenario.decisions.find(d => d.id === pendingTriggered[0].decisionId);
            if (nextDecision) {
                setActiveDecision(nextDecision);
                setDecisionMinimized(true); // Show minimized on recovery
            }
        }
    }, [scenario]);

    // Keep completedDecisions in sync with MV (comms-resolved decisions, etc.)
    useEffect(() => {
        if (!scenario) return;
        const mvDecisions = materializedView.completedDecisions || [];
        if (mvDecisions.length > completedDecisions.size) {
            const updated = new Map(completedDecisions);
            for (const cd of mvDecisions) {
                if (!updated.has(cd.decisionId)) {
                    const decision = scenario.decisions.find(d => d.id === cd.decisionId);
                    const option = decision?.options.find(o => o.id === cd.optionId);
                    updated.set(cd.decisionId, {
                        optionId: cd.optionId,
                        feedback: cd.feedback || option?.feedback || ''
                    });
                }
            }
            setCompletedDecisions(updated);
        }
    }, [scenario, materializedView.completedDecisions]);

    // Update feed when emails arrive
    const prevEmailCountRef = useRef(0);

    useEffect(() => {
        const inboxEmails = materializedView.inbox?.emails || [];
        const receivedEmails = inboxEmails.filter(e => e.from !== 'player');

        if (receivedEmails.length > prevEmailCountRef.current) {
            const newEmails = receivedEmails.slice(prevEmailCountRef.current);
            setFeedEntries(prev => [
                ...prev,
                ...newEmails.map(e => ({
                    id: `email-recv-${e.id}`,
                    type: 'email_received' as const,
                    timestamp: e.timestamp,
                    from: e.from,
                    subject: e.subject
                }))
            ]);
        }
        prevEmailCountRef.current = receivedEmails.length;
    }, [materializedView.inbox?.emails?.length]);

    // Proactive comms nudge: when NPC frustration > 0.3 and no interaction in 3+ minutes
    const lastNudgeRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!scenario) return;
        const rels = materializedView.relationships || {};
        const now = Date.now();

        for (const [npcId, rel] of Object.entries(rels)) {
            if (rel.frustration <= 0.3) continue;

            // Check time since last interaction
            const lastTime = rel.lastInteractionTime ? new Date(rel.lastInteractionTime).getTime() : 0;
            const minutesSinceInteraction = lastTime > 0 ? (now - lastTime) / 60000 : 999;

            if (minutesSinceInteraction < 3) continue;

            // Don't nudge more than once per 3 minutes per NPC
            const lastNudge = lastNudgeRef.current[npcId] ?? 0;
            if (now - lastNudge < 180000) continue;

            const npc = scenario.npcs.find(n => n.id === npcId);
            if (!npc) continue;

            lastNudgeRef.current[npcId] = now;

            // Build actionable nudge — suggest checking their emails or Slack
            const hasUnreadEmail = materializedView.inbox.emails.some(
                e => e.from !== 'player' && !e.isRead && e.from.toLowerCase().includes(npc.name.split(' ')[0].toLowerCase())
            );
            const suggestion = hasUnreadEmail
                ? `Check your inbox — ${npc.name} sent you an email.`
                : `Consider reaching out to ${npc.name} (${npc.role}) via email or Slack.`;

            setFeedEntries(prev => {
                const nudgeId = `nudge-${npcId}-${Math.floor(now / 180000)}`;
                if (prev.some(e => e.id === nudgeId)) return prev;
                return [...prev, {
                    id: nudgeId,
                    type: 'system' as const,
                    timestamp: new Date().toISOString(),
                    message: suggestion
                }];
            });
        }
    }, [scenario, materializedView.relationships, localSimTime]);

    // Watch for escalation level changes and add feed entries
    const prevEscalationRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!scenario) return;
        const rels = materializedView.relationships || {};

        for (const [npcId, rel] of Object.entries(rels)) {
            const prevLevel = prevEscalationRef.current[npcId] ?? 0;
            if (rel.escalationLevel > prevLevel) {
                const npc = scenario.npcs.find(n => n.id === npcId);
                const npcName = npc?.name || npcId;

                // Build actionable escalation message
                const action = rel.waitingFor
                    ? `Reply to their message or send them an update about: ${rel.waitingFor}`
                    : `Send ${npcName} a message via email or Slack.`;

                if (rel.escalationLevel === 1) {
                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `escalation-${npcId}-1`)) return prev;
                        return [...prev, {
                            id: `escalation-${npcId}-1`,
                            type: 'escalation' as const,
                            timestamp: new Date().toISOString(),
                            description: `${npcName} is getting impatient. ${action}`
                        }];
                    });
                } else if (rel.escalationLevel >= 2) {
                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `escalation-${npcId}-2`)) return prev;
                        return [...prev, {
                            id: `escalation-${npcId}-2`,
                            type: 'escalation' as const,
                            timestamp: new Date().toISOString(),
                            description: `${npcName} is frustrated — they've been waiting too long. ${action}`
                        }];
                    });
                }
            }
            prevEscalationRef.current[npcId] = rel.escalationLevel;
        }
    }, [scenario, materializedView.relationships]);

    // Watch for new triggered decisions from MV (pushed via WebSocket)
    const prevTriggeredRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!scenario) return;

        const triggered = materializedView.triggeredDecisions || [];
        const completedIds = new Set((materializedView.completedDecisions || []).map(cd => cd.decisionId));

        for (const t of triggered) {
            if (completedIds.has(t.decisionId)) continue;
            if (prevTriggeredRef.current.has(t.decisionId)) continue;

            // Check if this decision was resolved via comms (completed same tick as triggered)
            const justCompleted = (materializedView.completedDecisions || []).find(
                cd => cd.decisionId === t.decisionId
            );

            if (justCompleted) {
                // Was this comms-resolved? Check if it appeared simultaneously with trigger
                const decision = scenario.decisions.find(d => d.id === t.decisionId);
                if (decision) {
                    const npc = scenario.npcs.find(n =>
                        decision.storyline && n.role.toLowerCase().includes(decision.storyline)
                    );

                    setFeedEntries(prev => {
                        if (prev.some(e => e.id === `comms-resolved-${t.decisionId}`)) return prev;
                        return [...prev, {
                            id: `comms-resolved-${t.decisionId}`,
                            type: 'comms_resolved_decision' as const,
                            timestamp: new Date().toISOString(),
                            npcName: npc?.name || 'a stakeholder',
                            decisionPrompt: decision.prompt
                        }];
                    });
                }
                continue;
            }

            // New triggered decision that hasn't been completed — show modal
            const decision = scenario.decisions.find(d => d.id === t.decisionId);
            if (decision) {
                setActiveDecision(decision);
                setDecisionMinimized(false);

                setFeedEntries(prev => {
                    if (prev.some(e => e.id === `triggered-${t.decisionId}`)) return prev;
                    return [...prev, {
                        id: `triggered-${t.decisionId}`,
                        type: 'system',
                        timestamp: new Date().toISOString(),
                        message: 'A new decision requires your attention.'
                    }];
                });
            }
        }

        // Check if all decisions are complete
        if (scenario.decisions.length > 0 && completedIds.size >= scenario.decisions.length) {
            setFeedEntries(prev => {
                if (prev.some(e => e.id === 'system-decisions-complete')) return prev;
                return [...prev, {
                    id: 'system-decisions-complete',
                    type: 'system',
                    timestamp: new Date().toISOString(),
                    message: 'All decisions complete. Continue managing your inbox and communications until the session ends.'
                }];
            });
        }

        prevTriggeredRef.current = new Set(triggered.map(t => t.decisionId));
    }, [scenario, materializedView.triggeredDecisions]);

    // Dismiss modal if the active decision was resolved via comms
    useEffect(() => {
        if (!activeDecision || !scenario) return;

        const completedIds = new Set((materializedView.completedDecisions || []).map(cd => cd.decisionId));
        if (completedIds.has(activeDecision.id)) {
            const cd = (materializedView.completedDecisions || []).find(c => c.decisionId === activeDecision.id);
            const option = activeDecision.options.find(o => o.id === cd?.optionId);

            setFeedEntries(prev => {
                if (prev.some(e => e.id === `comms-resolved-${activeDecision.id}`)) return prev;
                return [...prev, {
                    id: `comms-resolved-${activeDecision.id}`,
                    type: 'comms_resolved_decision' as const,
                    timestamp: new Date().toISOString(),
                    npcName: 'your message',
                    decisionPrompt: activeDecision.prompt
                }];
            });

            if (cd && option) {
                setCompletedDecisions(prev => {
                    const updated = new Map(prev);
                    updated.set(activeDecision.id, {
                        optionId: cd.optionId,
                        feedback: cd.feedback || option.feedback || ''
                    });
                    return updated;
                });
            }

            setActiveDecision(null);
            setDecisionMinimized(false);
        }
    }, [activeDecision, scenario, materializedView.completedDecisions]);

    const handleDecisionChoose = useCallback(async (decisionId: string, optionId: string, timeCostSeconds: number) => {
        if (!scenario || pendingDecision) return;

        setPendingDecision(true);
        try {
            const result = await api.makeDecision(session.id, { decisionId, optionId, timeCostSeconds });

            const decision = scenario.decisions.find(d => d.id === decisionId);
            const option = decision?.options.find(o => o.id === optionId);
            const feedback = result.feedback || option?.feedback || '';

            const newCompleted = new Map(completedDecisions);
            newCompleted.set(decisionId, { optionId, feedback });
            setCompletedDecisions(newCompleted);

            // Close the modal — next decision will appear when backend triggers it
            setActiveDecision(null);
            setDecisionMinimized(false);

            // Add outcome to feed
            setFeedEntries(prev => [...prev, {
                id: `outcome-${decisionId}`,
                type: 'decision_outcome',
                timestamp: new Date().toISOString(),
                prompt: decision?.prompt || '',
                chosenText: option?.text || '',
                feedback
            }]);
        } catch (err) {
            console.error('Failed to submit decision:', err);
        } finally {
            setPendingDecision(false);
        }
    }, [scenario, session.id, completedDecisions, pendingDecision]);

    const handleCompleteSession = useCallback(async () => {
        if (completingSession) return;
        setCompletingSession(true);
        try {
            await api.completeSession(session.id);
        } catch (err) {
            console.error('Failed to complete session:', err);
            setCompletingSession(false);
        }
    }, [session.id, completingSession]);

    // Computed data
    const unlistenedVoicemailCount = (materializedView.phone?.voicemails || []).filter(v => !v.isListened).length;
    const timeRemainingSeconds = localTimeRemaining;
    const unreadCount = materializedView.inbox.emails.filter(e => e.to === 'player' && !e.isRead && !e.isArchived).length;
    const totalSlackMessages = Object.values(materializedView.slack?.channels || {}).reduce((t, c) => t + c.messages.length, 0)
        + Object.values(materializedView.slack?.directMessages || {}).reduce((t, msgs) => t + msgs.length, 0);

    const npcs = scenario?.npcs || [];
    const totalDecisions = scenario?.decisions.length || 5;
    const completedCount = completedDecisions.size;
    const allDecisionsComplete = totalDecisions > 0 && completedCount >= totalDecisions;

    const relationships = Object.entries(materializedView.relationships || {});
    const stakeholderHealth = relationships.length > 0
        ? Math.round(relationships.reduce((sum, [, rel]) => sum + ((rel.trust - rel.frustration) * 100), 0) / relationships.length)
        : 50;

    const meetingDeadline = materializedView.deadlines?.find(d => d.id === 'all-hands-meeting');
    const meetingMinutesLeft = meetingDeadline
        ? Math.max(0, Math.floor((meetingDeadline.atSimulationSeconds - localSimTime) / 60))
        : 99;

    const emailsReceived = materializedView.inbox.emails.filter(e => e.from !== 'player').length;
    const emailsRead = materializedView.inbox.emails.filter(e => e.from !== 'player' && e.isRead).length;
    const emailsSent = materializedView.inbox.emails.filter(e => e.from === 'player').length;
    const voicemailsListened = (materializedView.phone?.voicemails || []).filter(v => v.isListened).length;
    const totalVoicemails = (materializedView.phone?.voicemails || []).length;

    return (
        <div className="bg-slate-900 font-sans h-screen flex flex-col text-white overflow-hidden">
            <div className="flex-1 flex flex-col w-full relative overflow-hidden">

                {/* HEADER & KPIs */}
                <header className="px-4 pt-4 pb-2 sticky top-0 z-20 md:static md:px-8 md:py-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                <div className="size-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-lg border border-blue-400/30">
                                    P
                                </div>
                                <div className="absolute bottom-0 right-0 size-3 bg-green-400 rounded-full border-2 border-slate-900"></div>
                            </div>
                            <div>
                                <h2 className="text-xs text-slate-400 font-medium uppercase tracking-widest mb-0.5">Session {session.id.substring(0, 8)}</h2>
                                <h1 className="text-2xl font-bold text-white leading-none">{scenario?.title || session.scenarioId}</h1>
                            </div>
                        </div>

                        {/* KPI Status Bar */}
                        <div className="flex items-center gap-6 bg-slate-800 rounded-full px-6 py-3 border border-slate-700">
                            <button onClick={() => setShowDecisionHistory(true)} className="flex items-center gap-3 hover:opacity-80 transition-opacity" title="View decision history">
                                <div className="text-blue-400"><Assignment /></div>
                                <div className="text-left">
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Decisions</p>
                                    <p className="text-lg font-bold text-white leading-none font-mono">{completedCount}/{totalDecisions}</p>
                                </div>
                            </button>
                            <div className="w-px h-8 bg-slate-700"></div>
                            <div className="flex items-center gap-3">
                                <div className={stakeholderHealth >= 50 ? "text-green-400" : stakeholderHealth >= 25 ? "text-amber-400" : "text-red-400"}>
                                    <Favorite />
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Stakeholders</p>
                                    <p className={`text-lg font-bold leading-none font-mono ${stakeholderHealth >= 50 ? "text-green-400" : stakeholderHealth >= 25 ? "text-amber-400" : "text-red-400"}`}>
                                        {stakeholderHealth}%
                                    </p>
                                </div>
                            </div>
                            <div className="w-px h-8 bg-slate-700"></div>
                            <div className="flex items-center gap-3">
                                <div className={timeRemainingSeconds <= 120 ? "text-red-400" : timeRemainingSeconds <= 300 ? "text-amber-400" : "text-blue-400"}>
                                    <AccessTime />
                                </div>
                                <div>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Time Left</p>
                                    <p className={`text-lg font-bold leading-none font-mono ${timeRemainingSeconds <= 120 ? "text-red-400 animate-pulse" : timeRemainingSeconds <= 300 ? "text-amber-400" : "text-white"}`}>
                                        {formatTimeRemaining(timeRemainingSeconds)}
                                    </p>
                                </div>
                            </div>
                            {meetingDeadline && !meetingDeadline.passed && meetingMinutesLeft <= 5 && (
                                <>
                                    <div className="w-px h-8 bg-slate-700"></div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${meetingMinutesLeft <= 2 ? "bg-red-500/20 border border-red-500/40" : "bg-amber-500/20 border border-amber-500/40"}`}>
                                        <Warning fontSize="small" className={meetingMinutesLeft <= 2 ? "text-red-400 animate-pulse" : "text-amber-400"} />
                                        <span className={`text-xs font-bold ${meetingMinutesLeft <= 2 ? "text-red-400" : "text-amber-400"}`}>
                                            All-hands in {meetingMinutesLeft}m
                                        </span>
                                    </div>
                                </>
                            )}
                        </div>

                        <button className="hidden md:flex items-center justify-center size-10 rounded-full hover:bg-white/10 transition-colors text-slate-400 hover:text-white">
                            <Settings />
                        </button>
                    </div>
                </header>

                {/* Main Content Grid */}
                <div className="flex-1 overflow-y-auto no-scrollbar pb-32 md:pb-20 px-4 md:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">

                        {/* LEFT: Objective + Activity Feed + Contacts */}
                        <div className="lg:col-span-10 flex flex-col gap-4">
                            {/* Objective Banner */}
                            <section className="h-32 shrink-0">
                                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 relative overflow-hidden h-full flex flex-col justify-center">
                                    <div className="relative z-10 flex items-center justify-between gap-8 h-full">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="flex size-2 rounded-full bg-blue-400 animate-pulse"></span>
                                                <h3 className="text-blue-400 text-[10px] font-bold uppercase tracking-[0.2em]">Current Objective</h3>
                                            </div>
                                            <p className="text-white font-bold text-xl leading-tight tracking-tight">
                                                {scenario?.title || 'Loading scenario...'}
                                            </p>
                                            <p className="text-slate-300 text-xs mt-1">Navigate your inbox, respond to stakeholders, and make critical decisions.</p>
                                        </div>

                                        <div className="flex items-center gap-4 shrink-0">
                                            <div className="w-48 bg-slate-900 rounded-full h-2 border border-slate-700">
                                                <div
                                                    className="bg-blue-500 h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${totalDecisions > 0 ? (completedCount / totalDecisions) * 100 : 0}%` }}
                                                />
                                            </div>
                                            <span className="text-xl font-bold text-blue-400 font-mono">
                                                {totalDecisions > 0 ? Math.round((completedCount / totalDecisions) * 100) : 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Activity Feed — Mission Log */}
                            <section className="flex-1 min-h-[400px] bg-slate-800 border border-slate-700 rounded-2xl p-5 flex flex-col relative overflow-hidden">
                                <div className="flex items-center justify-between mb-4 border-b border-slate-700 pb-2">
                                    <div className="flex items-center gap-2">
                                        <Assignment className="text-blue-400" fontSize="small" />
                                        <h3 className="text-xs font-bold text-blue-400 uppercase tracking-widest">Mission Log</h3>
                                    </div>
                                    <div className="flex gap-2 items-center">
                                        <div className="size-2 rounded-full bg-blue-400 animate-pulse"></div>
                                        <span className="text-[10px] text-slate-500 uppercase">Live</span>
                                    </div>
                                </div>

                                <ActivityFeed
                                    entries={feedEntries}
                                />
                            </section>

                            {/* Key Contacts */}
                            <section>
                                <div className="flex items-center justify-between mb-3">
                                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest pl-1">Key Contacts</h3>
                                    <button onClick={() => setShowContactsModal(true)} className="text-[10px] font-bold text-blue-400 hover:text-blue-300 transition-colors uppercase tracking-wider hover:underline">View Directory</button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {npcs.slice(0, 3).map((npc) => {
                                        const rel = materializedView.relationships?.[npc.id];
                                        const health = rel ? Math.round((rel.trust - rel.frustration) * 100) : 50;
                                        const statusColor = health >= 50 ? 'bg-green-400' : health >= 25 ? 'bg-amber-400' : 'bg-red-400';
                                        const avatarUrl = NPC_AVATARS[npc.id];

                                        // Qualitative sentiment label — escalation level takes priority
                                        let sentimentReason = '';
                                        const sentimentLabel = !rel ? (sentimentReason = 'no relationship data', 'Neutral') :
                                            rel.escalationLevel >= 2 ? (sentimentReason = `escalationLevel=${rel.escalationLevel} (>=2)`, 'Frustrated') :
                                            rel.escalationLevel >= 1 ? (sentimentReason = `escalationLevel=${rel.escalationLevel} (>=1)`, 'Waiting') :
                                            rel.frustration >= 0.5 ? (sentimentReason = `frustration=${rel.frustration.toFixed(2)} (>=0.5)`, 'Frustrated') :
                                            rel.frustration >= 0.3 ? (sentimentReason = `frustration=${rel.frustration.toFixed(2)} (>=0.3)`, 'Waiting') :
                                            rel.trust >= 0.7 ? (sentimentReason = `trust=${rel.trust.toFixed(2)} (>=0.7)`, 'Satisfied') :
                                            rel.trust >= 0.4 ? (sentimentReason = `trust=${rel.trust.toFixed(2)} (>=0.4)`, 'Neutral') :
                                            (sentimentReason = `trust=${rel.trust.toFixed(2)} (<0.4)`, 'Concerned');
                                        console.log(
                                            `[NPC ${npc.name}] label=${sentimentLabel} reason=${sentimentReason}` +
                                            (rel ? ` | trust=${rel.trust.toFixed(2)} frustration=${rel.frustration.toFixed(2)} escalation=${rel.escalationLevel} waitingFor=${rel.waitingFor || 'none'} lastInteraction=${rel.lastInteractionTime || 'never'}` : '')
                                        );
                                        const sentimentColor = sentimentLabel === 'Satisfied' ? 'text-green-400' :
                                            sentimentLabel === 'Neutral' ? 'text-slate-400' :
                                            sentimentLabel === 'Waiting' ? 'text-amber-400' :
                                            sentimentLabel === 'Frustrated' ? 'text-red-400' : 'text-amber-400';

                                        return (
                                            <div
                                                key={npc.id}
                                                className="flex items-center gap-3 bg-slate-800 border border-slate-700 p-3 rounded-lg hover:border-blue-500/40 transition-all group cursor-pointer relative"
                                                title={rel ? `Trust: ${Math.round(rel.trust * 100)}% | Frustration: ${Math.round(rel.frustration * 100)}%` : 'No interactions yet'}
                                                onClick={() => { setSelectedNpcId(npc.id); setShowContactsModal(true); }}
                                            >
                                                <div className="relative shrink-0">
                                                    {avatarUrl ? (
                                                        <img
                                                            src={avatarUrl}
                                                            alt={npc.name}
                                                            className="size-10 rounded-full object-cover border border-slate-600 group-hover:border-blue-500/50 transition-colors"
                                                        />
                                                    ) : (
                                                        <div className="size-10 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-bold border border-slate-600">
                                                            {getInitials(npc.name)}
                                                        </div>
                                                    )}
                                                    <div className={`absolute bottom-0 right-0 size-2.5 rounded-full border-2 border-slate-800 ${statusColor}`}></div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="text-sm font-bold text-white truncate group-hover:text-blue-400 transition-colors">{npc.name}</h4>
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[10px] text-slate-400 truncate uppercase tracking-wide font-medium">{npc.role}</p>
                                                        <span className={`text-[10px] font-bold ${sentimentColor}`}>{sentimentLabel}</span>
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Forum fontSize="small" className="text-blue-400" style={{ fontSize: '1rem' }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        </div>

                        {/* RIGHT: Comms Hub + Session Activity */}
                        <div className="lg:col-span-2 flex flex-col gap-6">
                            <section className="bg-slate-800 border border-slate-700 rounded-xl p-4 flex flex-col h-full">
                                <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-4">Comms Hub</h3>

                                <div className="flex flex-col gap-2">
                                    <button onClick={() => setShowEmailModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Mail fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Inbox</span>
                                        </div>
                                        {unreadCount > 0 && (
                                            <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{unreadCount}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowPhoneModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Call fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Calls</span>
                                        </div>
                                        {unlistenedVoicemailCount > 0 && (
                                            <span className="text-[10px] font-bold bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded animate-pulse">{unlistenedVoicemailCount}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowSlackModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><Forum fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Channels</span>
                                        </div>
                                        {totalSlackMessages > 0 && (
                                            <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{totalSlackMessages}</span>
                                        )}
                                    </button>

                                    <button onClick={() => setShowCalendarModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><CalendarMonth fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Calendar</span>
                                        </div>
                                        <span className="text-[10px] font-bold bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{materializedView.calendar?.length ?? 0}</span>
                                    </button>

                                    <button onClick={() => setShowContactsModal(true)} className="flex items-center justify-between bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-blue-500/50 p-3 rounded-lg transition-all group w-full text-left">
                                        <div className="flex items-center gap-3">
                                            <div className="text-slate-400 group-hover:text-blue-400 transition-colors"><People fontSize="small" /></div>
                                            <span className="text-xs font-bold text-white group-hover:text-blue-400 transition-colors">Contacts</span>
                                        </div>
                                    </button>
                                </div>

                                {/* Session Activity */}
                                <div className="mt-auto pt-6 border-t border-slate-700">
                                    <h3 className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-3">Session Activity</h3>
                                    <div className="space-y-3">
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Emails Read</span>
                                                <span className="text-white font-bold">{emailsRead}/{emailsReceived}</span>
                                            </div>
                                            <div className="h-1 bg-slate-900 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${emailsReceived > 0 ? (emailsRead / emailsReceived) * 100 : 0}%` }}></div>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Emails Sent</span>
                                                <span className="text-white font-bold">{emailsSent}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Slack Messages</span>
                                                <span className="text-white font-bold">{totalSlackMessages}</span>
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex justify-between text-[10px] mb-1">
                                                <span className="text-slate-400 font-medium">Voicemails</span>
                                                <span className="text-white font-bold">{voicemailsListened}/{totalVoicemails}</span>
                                            </div>
                                            <div className="h-1 bg-slate-900 rounded-full overflow-hidden">
                                                <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${totalVoicemails > 0 ? (voicemailsListened / totalVoicemails) * 100 : 0}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>

            {/* Decision Modal (full or minimized pill) */}
            {activeDecision && !decisionMinimized && (
                <DecisionModal
                    decision={activeDecision}
                    onChoose={(optionId, timeCost) => {
                        handleDecisionChoose(activeDecision.id, optionId, timeCost);
                    }}
                    onMinimize={() => setDecisionMinimized(true)}
                    disabled={pendingDecision}
                />
            )}
            {activeDecision && decisionMinimized && (
                <button
                    onClick={() => setDecisionMinimized(false)}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-2.5 bg-slate-800 border border-blue-500/40 rounded-full px-4 py-2.5 shadow-lg shadow-blue-500/10 hover:border-blue-500/60 hover:bg-slate-700 transition-all group"
                >
                    <span className="relative flex size-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full size-2.5 bg-blue-400"></span>
                    </span>
                    <span className="text-xs font-bold text-blue-400 group-hover:text-blue-300">Decision pending</span>
                </button>
            )}

            {/* Email Modal */}
            <Dialog open={showEmailModal} onClose={() => { setShowEmailModal(false); setComposeToEmail(undefined); }} maxWidth="xl" fullWidth
                PaperProps={{ sx: { height: '90vh', bgcolor: '#1e293b' } }}>
                <EmailInterface session={session} materializedView={materializedView} initialComposeTo={composeToEmail} />
                <button onClick={() => { setShowEmailModal(false); setComposeToEmail(undefined); }}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Email">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Slack Modal */}
            <Dialog open={showSlackModal} onClose={() => setShowSlackModal(false)} maxWidth="xl" fullWidth
                PaperProps={{ sx: { height: '90vh', bgcolor: '#1e293b' } }}>
                <SlackInterface session={session} materializedView={materializedView} />
                <button onClick={() => setShowSlackModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Slack">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Calendar Modal */}
            <Dialog open={showCalendarModal} onClose={() => setShowCalendarModal(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '80vh', bgcolor: '#1e293b' } }}>
                <CalendarView materializedView={materializedView} />
                <button onClick={() => setShowCalendarModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Calendar">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Contacts Modal */}
            <Dialog open={showContactsModal} onClose={() => { setShowContactsModal(false); setSelectedNpcId(null); }} maxWidth="sm" fullWidth
                PaperProps={{ sx: { height: '70vh', bgcolor: '#1e293b', overflow: 'visible' } }}>
                <ContactsView session={session} materializedView={materializedView}
                    initialSelectedNpcId={selectedNpcId}
                    onSendEmail={(email) => { setShowContactsModal(false); setSelectedNpcId(null); setComposeToEmail(email); setShowEmailModal(true); }} />
                <button onClick={() => { setShowContactsModal(false); setSelectedNpcId(null); }}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Contacts">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Phone Modal */}
            <Dialog open={showPhoneModal} onClose={() => setShowPhoneModal(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { height: '70vh', bgcolor: '#1e293b' } }}>
                <PhoneView session={session} materializedView={materializedView} />
                <button onClick={() => setShowPhoneModal(false)}
                    className="fixed top-4 right-4 z-[60] p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-colors"
                    title="Close Phone">
                    <span className="text-white text-sm font-bold">✕ Close</span>
                </button>
            </Dialog>

            {/* Decision History Modal */}
            <Dialog open={showDecisionHistory} onClose={() => setShowDecisionHistory(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { maxHeight: '80vh', bgcolor: '#1e293b' } }}>
                <div className="p-6 text-white">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <Assignment className="text-blue-400" />
                            <h2 className="text-xl font-bold">Decision History</h2>
                            <span className="text-sm text-slate-400 font-mono">{completedCount}/{totalDecisions}</span>
                        </div>
                        <button onClick={() => setShowDecisionHistory(false)}
                            className="p-1 hover:bg-white/10 rounded-lg transition-colors">
                            <span className="text-slate-300 text-sm font-bold">✕</span>
                        </button>
                    </div>

                    {/* Progress bar */}
                    <div className="w-full h-2 bg-slate-700 rounded-full mb-6 overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${totalDecisions > 0 ? (completedCount / totalDecisions) * 100 : 0}%` }} />
                    </div>

                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
                        {scenario?.decisions.map((decision) => {
                            const completed = completedDecisions.get(decision.id);
                            const triggered = (materializedView.triggeredDecisions || []).some(t => t.decisionId === decision.id);
                            const commsResolved = completed && (materializedView.completedDecisions || []).find(
                                cd => cd.decisionId === decision.id
                            );
                            const isCommsResolved = commsResolved && feedEntries.some(e => e.id === `comms-resolved-${decision.id}`);
                            const chosenOption = completed ? decision.options.find(o => o.id === completed.optionId) : null;

                            return (
                                <div key={decision.id}
                                    className={`border rounded-xl p-4 ${
                                        completed ? 'border-green-500/30 bg-green-500/5' :
                                        triggered ? 'border-amber-500/30 bg-amber-500/5' :
                                        'border-slate-700 bg-slate-800/50'
                                    }`}>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <p className="text-sm font-medium text-slate-200 leading-relaxed flex-1">
                                            {(completed || triggered) ? decision.prompt : 'Decision not yet available'}
                                        </p>
                                        {completed ? (
                                            <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded ${
                                                isCommsResolved ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'
                                            }`}>
                                                {isCommsResolved ? 'Resolved via comms' : 'Completed'}
                                            </span>
                                        ) : triggered ? (
                                            <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-amber-500/20 text-amber-400">Active</span>
                                        ) : (
                                            <span className="shrink-0 text-[10px] font-bold px-2 py-1 rounded bg-slate-700 text-slate-400">Upcoming</span>
                                        )}
                                    </div>
                                    {completed && chosenOption && (
                                        <div className="mt-3 pl-3 border-l-2 border-green-500/30">
                                            <p className="text-xs text-green-400 font-bold mb-1">Your choice: {chosenOption.text}</p>
                                            {completed.feedback && (
                                                <p className="text-xs text-slate-400 leading-relaxed">{completed.feedback}</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Dialog>

            {/* Proceed to All-Hands button — shown when all decisions complete */}
            {allDecisionsComplete && !isSessionCompleted && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-fade-in">
                    <button
                        onClick={handleCompleteSession}
                        disabled={completingSession}
                        className="flex items-center gap-3 bg-blue-500 hover:bg-blue-400 disabled:opacity-50 text-white font-bold px-8 py-4 rounded-2xl shadow-2xl shadow-blue-500/30 transition-all hover:scale-105"
                    >
                        <Groups style={{ fontSize: '1.5rem' }} />
                        <span>{completingSession ? 'Wrapping up...' : 'Proceed to All-Hands Meeting'}</span>
                    </button>
                </div>
            )}

            {/* Session Completion Modal */}
            <SessionCompletionModal isOpen={isSessionCompleted} sessionId={session.id} playerInitiated={allDecisionsComplete} />
        </div>
    );
};

export default StitchDashboard;
